import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Box,
  Divider,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import { httpRequest } from 'src/services/httpRequest';
import ApplicationStatus from '../ApplicationStatus';
import ApplicationActions from '../ApplicationActions';
import { format } from 'date-fns';
import ApplicationListFilter from '../ApplicationListFilter';
import { useParams } from 'react-router-dom';
import './Table.css';

// Custom styled component for table cells with different styles for header and body
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main || '#6E759F',
    color: theme.palette.common.white,
    fontSize: '0.9rem',
    padding: '8px',
    textTransform: 'capitalize'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '8px'
  }
}));

const StyledHeaderSortLabel = styled(TableSortLabel)(({ theme }) => ({
  color: `${theme.palette.common.white} !important`,
  '&:hover': {
    color: `${theme.palette.common.white} !important`
  },
  '&.Mui-active': {
    color: `${theme.palette.common.white} !important`
  },
  '& .MuiTableSortLabel-icon': {
    color: `${theme.palette.common.white} !important`
  }
}));

// Custom styled component for table rows
const StyledTableRow = styled(TableRow)(() => ({
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

type PetAllAppsSortApiKey =
  | 'LicenseNumber'
  | 'ApplicantName'
  | 'OwnerName'
  | 'PetType'
  | 'PetName';

/** Pet License “All Applications” column headers aligned to backend SortBy */
const PET_LICENSE_APP_SORT: Partial<
  Record<string, PetAllAppsSortApiKey>
> = {
  'License Number': 'LicenseNumber',
  'Applicant Name': 'ApplicantName',
  'Owner Name': 'OwnerName',
  'Pet Type': 'PetType',
  'Pet Name': 'PetName'
};

type SortCycle = 'default' | 'asc' | 'desc';

/**
 * CustomTable Component
 * @param {string[]} columns - Array of column names to display
 * @param {boolean} isDraft - Flag to indicate if showing draft applications
 */
export default function CustomTable ({ columns = [], isDraft = false }) {
  const { permit } = useParams();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [TotalDataCount, setTotalDataCount] = React.useState(0);
  /** Refresh overlay on table/pagination area only — not full screen */
  const [tableLoading, setTableLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [selectVal, setSelectVal] = React.useState('ALL');
  const [searchText, setSearchText] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [applicationSort, setApplicationSort] = React.useState<{
    key: PetAllAppsSortApiKey | null;
    dir: SortCycle;
  }>({ key: null, dir: 'default' });

  const dispatch = useDispatch();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const permitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );

  const displayColumns = isDraft ? columns.slice(1) : columns;

  const isPetLicenseAllApplicationsTable =
    !isDraft &&
    columns.includes('License Number') &&
    columns.includes('Pet Type') &&
    columns.includes('Pet Name');

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Pet type: toggle cats-first / dogs-first only (matches My Pets).
   * Other sortable columns: default → asc → desc → default.
   */
  const toggleApplicationColumnSort = (heading: string) => {
    const apiKey = PET_LICENSE_APP_SORT[heading];
    if (!apiKey) return;

    setPage(0);
    setApplicationSort((prev) => {
      if (prev.key !== apiKey) {
        return { key: apiKey, dir: 'asc' };
      }
      if (apiKey === 'PetType') {
        return {
          key: 'PetType',
          dir: prev.dir === 'asc' ? 'desc' : 'asc'
        };
      }
      if (prev.dir === 'default') return { key: apiKey, dir: 'asc' };
      if (prev.dir === 'asc') return { key: apiKey, dir: 'desc' };
      return { key: null, dir: 'default' };
    });
  };

  const fetchListData = async (): Promise<void> => {
    setTableLoading(true);
    try {
      let url = `/applications/${permitDetails.id || permit}/get_all?Page=${
        page + 1
      }&Size=${rowsPerPage}&Filter=${isDraft ? 'Draft' : selectVal}`;

      if (permitDetails?.currentPermitUserRole === 'Vendor') {
        url += `&UserID=${userDetails.UserID}`;
      }

      if (debouncedSearch.trim()) {
        url += `&Search=${encodeURIComponent(debouncedSearch.trim())}`;
      }

      if (
        isPetLicenseAllApplicationsTable &&
        applicationSort.key != null &&
        applicationSort.dir !== 'default'
      ) {
        url += `&SortBy=${applicationSort.key}&SortDirection=${applicationSort.dir}`;
      }

      const data = await httpRequest('GET', url);

      if (data?.status === 'success') {
        const formattedRows = [];
        const rowData = columns.map((item: string) => item.replace(/\s+/g, ''));

        // Row shape varies by permit — keep loose typing for formatter
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data?.data?.data?.forEach((item: any) => {
          const rowObj = {};
          rowData.forEach((rowName: string) => {
            if (rowName === 'Status') {
              rowObj[rowName] = (
                <ApplicationStatus
                  status={item?.ApplicationStatus}
                  permitId={permitDetails.id}
                />
              );
            } else if (rowName === 'Action' || rowName === 'Actions') {
              rowObj[rowName] = (
                <ApplicationActions
                  item={item}
                  isDraft={isDraft}
                  handleFetchApplications={fetchListData}
                />
              );
            } else if (rowName === 'LastActivityDate') {
              rowObj[rowName] = format(new Date(item.UpdateDate), 'MM/dd/yyyy');
            } else if (rowName === 'PermitExpirationDate') {
              rowObj[rowName] = item?.PermitExpirationDate
                ? format(new Date(item?.PermitExpirationDate), 'MM/dd/yyyy')
                : '-';
            } else {
              rowObj[rowName] = item[rowName] || '';
            }
          });
          formattedRows.push(rowObj);
        });

        setTotalDataCount(data?.data?.totalRecords);
        setRows(formattedRows);
      } else {
        if (data?.type === 'NETWORK_ERROR') {
          toast.error('Network error occurred');
        } else {
          toast.error(data?.message);
          dispatch(handleError(data?.message));
        }
      }
    } catch (error) {
      toast.error('An error occurred');
      dispatch(handleError('An error occurred'));
    } finally {
      setTableLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  React.useEffect(() => {
    void fetchListData();
  }, [
    selectVal,
    rowsPerPage,
    page,
    debouncedSearch,
    isPetLicenseAllApplicationsTable,
    applicationSort.key,
    applicationSort.dir
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          width: '100%',
          flexWrap: { sm: 'nowrap' },
          pt: { sm: 0.5 },
          overflow: 'visible'
        }}
      >
        <Typography
          fontWeight={'bold'}
          fontSize={{ md: '1.8rem', xs: '1.4rem' }}
          color={'#6E759F'}
          sx={{ flexShrink: 0, lineHeight: 1.2 }}
        >
          {isDraft ? 'Draft' : 'All'} Applications
        </Typography>

        {!isDraft && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
              marginLeft: { xs: 0, sm: 'auto' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              overflow: 'visible'
            }}
          >
            <TextField
              size="medium"
              placeholder={
                isPetLicenseAllApplicationsTable
                  ? 'Search'
                  : 'Search'
              }
              value={searchText}
              onChange={handleSearchChange}
              sx={{
                width: { xs: '100%', sm: 260 },
                minWidth: { sm: 200 },
                maxWidth: { sm: 360 }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
            <ApplicationListFilter
              filterValue={selectVal}
              setFilterValue={setSelectVal}
              title={'Status'}
              options={
                permit === '4'
                  ? [
                      { key: 'All', value: 'ALL' },
                      { key: 'Await CO', value: 'Await CO' },
                      { key: 'Await Vendor', value: 'Await Vendor' },
                      { key: 'Approved', value: 'Approved' },
                      { key: 'Issued', value: 'Issued' },
                      { key: 'Rejected', value: 'Rejected' }
                    ]
                  : [
                      { key: 'All', value: 'ALL' },
                      { key: 'In Review', value: 'In Review' },
                      { key: 'Approved', value: 'Approved' },
                      { key: 'Issued', value: 'Issued' },
                      { key: 'Expired', value: 'Expired' },
                      { key: 'Rejected', value: 'Rejected' }
                    ]
              }
            />
          </Box>
        )}
      </Box>

      <Box
        mt={2}
        sx={{
          width: '100%',
          overflowX: 'auto',
          position: 'relative',
          minHeight: 200
        }}
      >
        {tableLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              zIndex: 3,
              pointerEvents: 'none'
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <TableContainer component={Paper} className="table_container_list">
          <Table
            aria-label="customized table"
            sx={{ position: 'relative', opacity: tableLoading ? 0.55 : 1 }}
          >
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <TableRow>
                {displayColumns?.map((heading, index) => {
                  const sortKey = PET_LICENSE_APP_SORT[heading];
                  const sortablePetHeader =
                    isPetLicenseAllApplicationsTable && !!sortKey;

                  return (
                    <StyledTableCell
                      key={index}
                      align={heading !== 'Picture' ? 'center' : 'left'}
                    >
                      {sortablePetHeader ? (
                        <StyledHeaderSortLabel
                          active={
                            applicationSort.key === sortKey &&
                            applicationSort.dir !== 'default'
                          }
                          direction={
                            applicationSort.dir === 'desc' ? 'desc' : 'asc'
                          }
                          onClick={() =>
                            toggleApplicationColumnSort(heading)
                          }
                        >
                          {heading}
                        </StyledHeaderSortLabel>
                      ) : (
                        heading
                      )}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows?.length > 0 ? (
                rows.map((row, index) => (
                  <StyledTableRow key={index}>
                    {Object.keys(row).map((key, keyIndex) => (
                      <React.Fragment key={key}>
                        {(!isDraft || keyIndex > 0) && key !== 'id' && (
                          <StyledTableCell
                            align="center"
                            sx={{ maxWidth: '30rem' }}
                          >
                            {row[key]}
                          </StyledTableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell
                    colSpan={displayColumns.length}
                    align="center"
                  >
                    <Typography variant="h6">
                      {tableLoading ? 'Loading…' : 'No data found'}
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ marginTop: 1 }} />
        </TableContainer>
        <TablePagination
          sx={{ position: 'sticky', bottom: 0 }}
          className="pagination_parent_wrapper"
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={TotalDataCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={tableLoading}
        />
      </Box>
    </>
  );
}
